/**
 * CurrentSystemRealityCard Component
 *
 * Paints the REALITY of keeping the current system.
 * Shows 5-year cost projection with energy, maintenance, and failure probability.
 * Creates natural motivation to consider replacement without being pushy.
 */

import { useState } from 'react';

export default function CurrentSystemRealityCard({
  currentAge,
  currentSystemProjection
}) {
  const projection = currentSystemProjection;
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '25px'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #f59e0b',
        paddingBottom: '15px',
        marginBottom: '20px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#f59e0b',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          If You Keep Your Current System
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '24px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          5-Year Cost Projection
        </h3>
        <p style={{
          margin: '8px 0 0 0',
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          Here's what you can expect if you continue with your {currentAge}-year-old system:
        </p>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        style={{
          width: '100%',
          padding: '12px 20px',
          marginBottom: '20px',
          background: showDetails ? '#fef3c7' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: showDetails ? '#92400e' : 'white',
          border: showDetails ? '2px solid #fcd34d' : 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => {
          if (!showDetails) {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!showDetails) {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }
        }}
      >
        <span>{showDetails ? '↑' : '↓'}</span>
        <span>{showDetails ? 'Hide' : 'Show'} Year-by-Year Breakdown</span>
      </button>

      {/* Year-by-Year Breakdown */}
      {showDetails && (
        <div style={{
          marginBottom: '25px',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}>
          <div style={{
            display: 'grid',
            gap: '10px'
          }}>
            {projection.projections.map((year, index) => (
            <div
              key={year.year}
              style={{
                padding: '15px',
                background: index % 2 === 0 ? '#f8fafc' : 'white',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <div style={{
                  fontWeight: '700',
                  fontSize: '16px',
                  color: '#1e293b'
                }}>
                  Year {year.year} (Age {year.systemAge})
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1e293b'
                }}>
                  ${year.totalCost.toLocaleString()}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                fontSize: '13px'
              }}>
                <div>
                  <span style={{ color: '#64748b' }}>Energy:</span>{' '}
                  <strong>${year.energyCost.toLocaleString()}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Maintenance:</span>{' '}
                  <strong>${year.maintenanceCost.toLocaleString()}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Failure Risk:</span>{' '}
                  <strong style={{ color: year.failureProbability > 35 ? '#ef4444' : '#f59e0b' }}>
                    {year.failureProbability}%
                  </strong>
                </div>
                {year.expectedRepairCost > 0 && (
                  <div>
                    <span style={{ color: '#64748b' }}>Expected Repairs:</span>{' '}
                    <strong style={{ color: '#ef4444' }}>
                      ${year.expectedRepairCost.toLocaleString()}
                    </strong>
                  </div>
                )}
              </div>

              {/* Cumulative Total */}
              <div style={{
                marginTop: '10px',
                paddingTop: '10px',
                borderTop: '1px solid #e2e8f0',
                fontSize: '13px',
                color: '#64748b'
              }}>
                Cumulative Total: <strong style={{ color: '#1e293b' }}>
                  ${year.cumulativeTotal.toLocaleString()}
                </strong>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {/* 5-Year Totals */}
      <div style={{
        padding: '20px',
        background: '#fffbeb',
        borderRadius: '10px',
        border: '2px solid #f59e0b',
        marginBottom: '20px'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#92400e',
          marginBottom: '15px'
        }}>
          5-Year Total Costs:
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '15px',
          marginBottom: '15px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>
              Energy Costs
            </div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
              ${projection.totals.energy.toLocaleString()}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>
              Maintenance
            </div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
              ${projection.totals.maintenance.toLocaleString()}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>
              Expected Repairs
            </div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#ef4444' }}>
              ${projection.totals.repairs.toLocaleString()}
            </div>
          </div>
        </div>

        <div style={{
          paddingTop: '15px',
          borderTop: '2px solid #fcd34d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#92400e' }}>
            Grand Total (5 Years)
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#d97706' }}>
            ${projection.totals.grand.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Risk Warning */}
      <div style={{
        padding: '15px 20px',
        background: '#fee2e2',
        borderLeft: '4px solid #ef4444',
        borderRadius: '6px'
      }}>
        <div style={{
          fontWeight: '700',
          color: '#991b1b',
          marginBottom: '6px',
          fontSize: '14px'
        }}>
          ⚠️ Increasing Failure Risk
        </div>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#7f1d1d',
          lineHeight: '1.6'
        }}>
          As your system ages, the probability of major component failure increases dramatically.
          The "Expected Repairs" shown above represent the <em>average</em> cost based on failure probability.
          A single compressor or heat exchanger failure could cost $1,400-$1,800+
          and leave you without heating or cooling when you need it most.
        </p>
      </div>
    </div>
  );
}
